import React, { Component } from 'react';
import { Alert, Breadcrumb, Button, Card, Select, Table, Tag } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';

import AntIcon from '../shared/components/AntIcon';
import { getApiErrorMessage, getGiveawayWinners, listGiveawayCampaigns } from './api';
import WinnerInfosDisplay from './components/WinnerInfosDisplay';
import { GIVEAWAY_STATUS_OPTIONS, getGiveawayStatusColor } from './status';
import { formatWinnerProgress, formatWinnersLabel, isSelectionComplete } from './winners';

class List extends Component {
  state = {
    campaignStatus: '',
    campaigns: [],
    campaignsError: '',
    campaignsLoading: true,
    campaignsPagination: {
      page: 1,
      limit: 20,
      totalCount: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
    winners: [],
    winnersError: '',
    winnersLoading: true,
    winnersPagination: {
      page: 1,
      limit: 10,
      totalCount: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };

  componentDidMount() {
    this.loadCampaigns();
    this.loadWinners();
  }

  loadCampaigns = ({ page = 1, limit = this.state.campaignsPagination.limit, status = this.state.campaignStatus } = {}) => {
    this.setState({ campaignsLoading: true, campaignsError: '', campaignStatus: status });
    return listGiveawayCampaigns({ page, limit, status: status || undefined })
      .then((response) => {
        this.setState({
          campaigns: response.data || [],
          campaignsPagination: response.pagination || {
            page,
            limit,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
          },
        });
      })
      .catch((error) => {
        this.setState({
          campaignsError: getApiErrorMessage(error, 'Failed to load giveaway campaigns.'),
        });
      })
      .finally(() => {
        this.setState({ campaignsLoading: false });
      });
  }

  loadWinners = ({ page = 1, limit = this.state.winnersPagination.limit } = {}) => {
    this.setState({ winnersLoading: true, winnersError: '' });
    return getGiveawayWinners({ page, limit })
      .then((response) => {
        this.setState({
          winners: response.data || [],
          winnersPagination: response.pagination || {
            page,
            limit,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
          },
        });
      })
      .catch((error) => {
        this.setState({
          winnersError: getApiErrorMessage(error, 'Failed to load giveaway winners history.'),
        });
      })
      .finally(() => {
        this.setState({ winnersLoading: false });
      });
  }

  renderCampaignTable() {
    const { campaigns, campaignsLoading, campaignsPagination } = this.state;
    const columns = [
      {
        title: 'Month',
        dataIndex: 'slugMonth',
        width: 110,
      },
      {
        title: 'Title',
        dataIndex: 'title',
        render: (title, record) => <Link to={`/giveaway/${record._id}/edit`}>{title}</Link>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 150,
        render: (status) => <Tag color={getGiveawayStatusColor(status)}>{status}</Tag>,
      },
      {
        title: 'Selection Date (UTC)',
        dataIndex: 'selectionDateUtc',
        width: 200,
        render: (value) => (value ? moment.utc(value).format('YYYY-MM-DD HH:mm [UTC]') : '-'),
      },
      {
        title: 'Entries',
        dataIndex: 'totalEntries',
        width: 100,
        render: (value) => value == null ? '-' : value,
      },
      {
        title: 'Participants',
        dataIndex: 'totalParticipants',
        width: 120,
        render: (value) => value == null ? '-' : value,
      },
      {
        title: 'Winners',
        key: 'winners',
        width: 240,
        render: (_, record) => (
          <div>
            <WinnerInfosDisplay campaign={record} showProgress={false} size={22} />
            <div style={{ marginTop: 4 }}>
              <Tag color={isSelectionComplete(record) ? 'blue' : 'orange'}>
                {formatWinnerProgress(record)}
              </Tag>
            </div>
          </div>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 180,
        render: (_, record) => (
          <Button.Group size="small">
            <Button>
              <Link to={`/giveaway/${record._id}/edit`}>Edit</Link>
            </Button>
            <Button>
              <Link to={`/giveaway/${record._id}/participants`}>Participants</Link>
            </Button>
          </Button.Group>
        ),
      },
    ];

    return (
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={campaigns}
        loading={campaignsLoading}
        scroll={{ x: 1200 }}
        pagination={{
          current: campaignsPagination.page,
          pageSize: campaignsPagination.limit,
          total: campaignsPagination.totalCount,
          showSizeChanger: true,
          onChange: (page, pageSize) => this.loadCampaigns({ page, limit: pageSize }),
          onShowSizeChange: (page, pageSize) => this.loadCampaigns({ page, limit: pageSize }),
          showTotal: (total) => `Total ${total} campaigns`,
        }}
      />
    );
  }

  renderWinnersTable() {
    const { winners, winnersLoading, winnersPagination } = this.state;
    const columns = [
      {
        title: 'Month',
        key: 'slugMonth',
        width: 110,
        render: (_, row) => (row.campaign && row.campaign.slugMonth) || '-',
      },
      {
        title: 'Campaign',
        key: 'title',
        render: (_, row) => (row.campaign && row.campaign.title) || '-',
      },
      {
        title: 'Winners',
        key: 'winners',
        render: (_, row) => {
          const source = {
            winnerInfos: row.winnerInfos
              || (row.campaign && row.campaign.winnerInfos)
              || undefined,
            winnerInfo: row.winnerInfo || (row.campaign && row.campaign.winnerInfo),
            winners: row.winners || (row.campaign && row.campaign.winners),
            winnerCount: (row.campaign && row.campaign.winnerCount) || row.winnerCount,
            requiredWinnerCount: (row.campaign && row.campaign.requiredWinnerCount) || row.requiredWinnerCount,
            selectionComplete: row.selectionComplete
              || (row.campaign && row.campaign.selectionComplete),
            status: row.campaign && row.campaign.status,
          };
          const label = formatWinnersLabel(source, '-');
          if (label === '-') return '-';
          return (
            <div>
              <WinnerInfosDisplay campaign={source} showProgress={false} size={22} />
            </div>
          );
        },
      },
      {
        title: 'Selected At',
        key: 'updatedAt',
        width: 180,
        render: (_, row) => {
          const value = row.campaign && row.campaign.updatedAt;
          return value ? moment.utc(value).format('YYYY-MM-DD HH:mm [UTC]') : '-';
        },
      },
    ];

    return (
      <Table
        rowKey={(row) =>
          `${(row.campaign && row.campaign._id) || 'campaign'}-${(row.winnerInfo && row.winnerInfo._id) || (row.winnerInfos && row.winnerInfos[0] && row.winnerInfos[0]._id) || 'winner'}`
        }
        columns={columns}
        dataSource={winners}
        loading={winnersLoading}
        pagination={{
          current: winnersPagination.page,
          pageSize: winnersPagination.limit,
          total: winnersPagination.totalCount,
          showSizeChanger: true,
          onChange: (page, pageSize) => this.loadWinners({ page, limit: pageSize }),
          onShowSizeChange: (page, pageSize) => this.loadWinners({ page, limit: pageSize }),
          showTotal: (total) => `Total ${total} winners`,
        }}
      />
    );
  }

  render() {
    const { campaignsError, campaignStatus, winnersError } = this.state;

    return (
      <div id="giveaway-list">
        <Breadcrumb>
          <Breadcrumb.Item>Giveaway</Breadcrumb.Item>
        </Breadcrumb>

        <div className="container">
          <h4>
            Giveaway Admin
            <Button.Group size="small" className="is-right">
              <Button onClick={() => this.loadCampaigns()}>
                <AntIcon type="reload" /> Refresh
              </Button>
              <Button>
                <Link to="/giveaway/new"><AntIcon type="plus" /> New Campaign</Link>
              </Button>
            </Button.Group>
          </h4>

          <div style={{ marginBottom: 16, maxWidth: 320, width: '100%' }}>
            <div style={{ marginBottom: 6 }}>Filter by status</div>
            <Select
              className="is-fullwidth"
              value={campaignStatus}
              onChange={(status) => this.loadCampaigns({ page: 1, status })}
            >
              <Select.Option value="">All statuses</Select.Option>
              {GIVEAWAY_STATUS_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          {campaignsError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={campaignsError}
            />
          )}

          <Card title="Campaign History" style={{ marginBottom: 16 }}>
            {this.renderCampaignTable()}
          </Card>

          <Card title="Winners History">
            {winnersError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={winnersError}
              />
            )}
            {this.renderWinnersTable()}
          </Card>
        </div>
      </div>
    );
  }
}

export default List;
